import MobileAuthLayout from './MobileAuthLayout';
import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Trans, useTranslation } from 'react-i18next';
import { ssoIdentityLineKey } from 'app/auth/ssoAttemptLog';
import { isMobileAppShell } from 'app/auth/nativePasskeyBridge';
import { isRepeatSwitchReturn, openMicrosoftSignOutPage } from 'app/auth/ssoAccountVerification';
import { sentryMask } from 'sentryReplayPrivacy';

/**
 * SEE-1200: SSO-failure screen for the mobile shell, mirroring the web
 * account-mismatch page (SEE-1170 copy). Shown by MobileSignIn instead of
 * silently landing the user back on the login form.
 *
 * SEE-1251 adds the wrong-account escape: when the IdP is Microsoft (and not
 * the iOS shell, whose ephemeral window can't hold a stale session), a
 * "Sign out of Microsoft" action ends the stuck Entra session in the system
 * browser so the next attempt shows the account picker. When the gate reports
 * which account Microsoft actually authenticated, both emails are listed.
 */

const Title = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 30,
  fontWeight: 700,
  lineHeight: '40px',
  marginBottom: theme.spacing(2),
}));

const Body = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 16,
  lineHeight: '24px',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1.5, 4),
  textTransform: 'none',
  fontWeight: 600,
}));

// Same identity treatment as the web screens: own line, strong dark text,
// long addresses wrap at the container instead of breaking mid-word in prose.
const IdentityEmphasis = styled('strong')(({ theme }) => ({
  display: 'block',
  color: theme.palette.text.primary,
  fontWeight: 700,
  overflowWrap: 'anywhere',
  margin: '6px 0',
}));

export interface MobileSsoErrorScreenProps {
  /** Account authenticated at the IdP but isn't provisioned in Speedchain. */
  isNotProvisioned: boolean;
  /** Email the user typed on the login form (lowercased). */
  typedEmail?: string;
  /** Identity the IdP actually authenticated, when it differs from typedEmail. */
  returnedIdentity?: string;
  onBackToLogin: () => void;
  /** SEE-1350: sign the wrong Microsoft account out and retry with the typed
   * email. Omitted (e.g. no typed email survived the redirect) hides the
   * button. */
  onUseDifferentAccount?: () => void;
}

function MobileSsoErrorScreen({
  isNotProvisioned,
  typedEmail,
  returnedIdentity,
  onBackToLogin,
  onUseDifferentAccount,
}: MobileSsoErrorScreenProps): JSX.Element {
  const { t } = useTranslation();
  const identityLineKey = ssoIdentityLineKey(typedEmail, returnedIdentity);
  // Web only: a switch attempt that returned the same wrong account means the
  // Entra session must be ended from Microsoft's own sign-out page — the shell
  // clears its cached sessions in the sheet chain instead.
  const switchRepeat =
    !isMobileAppShell() && !!onUseDifferentAccount && isRepeatSwitchReturn(returnedIdentity);
  return (
    <MobileAuthLayout
      footer={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
          {switchRepeat && (
            <ActionButton fullWidth variant="contained" onClick={() => openMicrosoftSignOutPage()}>
              {t('ssoMismatch.signOutOfMicrosoft', 'Sign out of Microsoft')}
            </ActionButton>
          )}
          {onUseDifferentAccount && (
            <ActionButton
              fullWidth
              variant={switchRepeat ? 'outlined' : 'contained'}
              onClick={onUseDifferentAccount}
            >
              {switchRepeat
                ? t('ssoMismatch.signedOutTryAgain', "I've signed out — try again")
                : t('ssoUseDifferentAccountButton', 'Use a different Microsoft account')}
            </ActionButton>
          )}
          <ActionButton
            fullWidth
            variant={onUseDifferentAccount || switchRepeat ? 'outlined' : 'contained'}
            onClick={onBackToLogin}
          >
            {t('ssoNotProvisionedButton')}
          </ActionButton>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Title variant="h4">{t('ssoNotProvisionedTitle')}</Title>
        {identityLineKey && (
          <Body>
            <Trans
              i18nKey={identityLineKey}
              values={{ typed: typedEmail, returned: returnedIdentity }}
              components={{ b: <IdentityEmphasis {...sentryMask} /> }}
            />
          </Body>
        )}
        <Body>{t(isNotProvisioned ? 'ssoNotProvisionedBody' : 'ssoErrorGenericBody')}</Body>
      </Box>
    </MobileAuthLayout>
  );
}

export default MobileSsoErrorScreen;
